// Legacy client-side salt personality classifier. The API now emits
// `deck.details.salt.personality` directly — this module exists only to
// classify decks whose cached payload predates the backend rollout.
// Delete after one release cycle of full backend coverage.

const PERSONALITY_RULES = [
    {
        key: 'mld',
        label: 'Earth-Scorcher',
        hint: 'Mass Land Destruction is the primary salt driver.',
        match: (s) => s.mld >= 0.3,
    },
    {
        key: 'stax',
        label: 'Stax Overlord',
        hint: 'Your “can’t do anything” effects dominate.',
        match: (s) => s.stax >= 0.25,
    },
    {
        key: 'combo',
        label: 'Combo Assassin',
        hint: 'Infinite combos make up a large share of the salt.',
        match: (s) => s.infiniteCombos >= 0.2,
    },
    {
        key: 'wipes',
        label: 'Reset Button',
        hint: 'Boardwipes + sweepers drive the salt.',
        match: (s) => s.boardwipes >= 0.2,
    },
    {
        key: 'reservedPrice',
        label: 'Wallet Flex',
        hint: 'Reserved-list + expensive cards carry the salt load.',
        match: (s) => s.reserved + s.cardPrice >= 0.25,
    },
    {
        key: 'wheelsDiscard',
        label: 'Discard Dom',
        hint: 'Wheels and discard dominate the salt profile.',
        match: (s) => s.wheels + s.discard >= 0.2,
    },
    {
        key: 'theft',
        label: 'Kleptomaniac',
        hint: 'Theft effects make up a meaningful share.',
        match: (s) => s.theft >= 0.1,
    },
    {
        key: 'extraCombats',
        label: 'Attack Step Addict',
        hint: 'Extra-combats + extra-turns drive the salt.',
        match: (s) => s.extraCombats + s.extraTurns >= 0.15,
    },
    {
        key: 'annihilator',
        label: 'Annihilator Abuser',
        hint: 'Eldrazi-style annihilator contributes noticeable salt.',
        match: (s) => s.annihilator >= 0.1,
    },
    {
        key: 'groupSlug',
        label: 'Pain Enthusiast',
        hint: 'Group-slug effects are a noticeable share.',
        match: (s) => s.groupSlug >= 0.1,
    },
];

function buildShares(totalsByCat, totalSalt) {
    const safe = (n) => (totalSalt > 0 ? n / totalSalt : 0);
    const stax =
        (totalsByCat.cantActivate || 0) +
        (totalsByCat.cantAttack || 0) +
        (totalsByCat.cantCast || 0) +
        (totalsByCat.cantDraw || 0) +
        (totalsByCat.cantEven || 0) +
        (totalsByCat.cantGainLife || 0) +
        (totalsByCat.cantSearch || 0) +
        (totalsByCat.cantTrigger || 0) +
        (totalsByCat.cantUntap || 0) +
        (totalsByCat.taxes || 0);
    return {
        mld: safe(totalsByCat.mld || 0),
        boardwipes: safe(totalsByCat.boardwipes || 0),
        stax: safe(stax),
        infiniteCombos: safe(totalsByCat.infiniteCombos || 0),
        reserved: safe(totalsByCat.reserved || 0),
        cardPrice: safe(totalsByCat.cardPrice || 0),
        wheels: safe(totalsByCat.wheels || 0),
        discard: safe(totalsByCat.discard || 0),
        theft: safe(totalsByCat.theft || 0),
        extraCombats: safe(totalsByCat.extraCombats || 0),
        extraTurns: safe(totalsByCat.extraTurns || 0),
        annihilator: safe(totalsByCat.annihilator || 0),
        groupSlug: safe(totalsByCat.groupSlug || 0),
    };
}

function classifyLegacy(shares) {
    const hits = PERSONALITY_RULES.filter((rule) => rule.match(shares));
    if (hits.length === 0) {
        return [
            {
                key: 'balanced',
                label: 'Balanced Salt',
                hint: 'No single category dominates. The salt is spread across many categories.',
            },
        ];
    }
    if (hits.length >= 3) {
        return [
            {
                key: 'stacked',
                label: 'Stacked Spite',
                hint: 'Multiple salt categories are all pulling serious weight.',
            },
            ...hits.slice(0, 2),
        ];
    }
    return hits.slice(0, 2);
}

function emptyEvidence() {
    return { categories: [], topCards: [], pieceCount: 0, shareOfDeckSalt: 0 };
}

/**
 * Compute a legacy-shaped personality from raw `categories` and `totalSalt`,
 * then adapt it to the new backend contract so the UI has a single rendering
 * path. Returns null when there's nothing to classify.
 */
export function computeFallbackPersonality(categories, totalSalt) {
    if (!categories || typeof categories !== 'object') return null;
    const totalsByCat = {};
    Object.keys(categories).forEach((catId) => {
        const list = categories[catId]?.list || [];
        totalsByCat[catId] = list.reduce((acc, entry) => acc + Number(entry?.score || 0), 0);
    });
    const shares = buildShares(totalsByCat, totalSalt);
    const hits = classifyLegacy(shares);
    if (!hits.length) return null;
    return {
        archetypes: hits.map((r, idx) => ({
            key: r.key,
            family: 'mixed',
            label: r.label,
            labelVariant: 'normal',
            tier: idx === 0 ? 'primary' : 'secondary',
            score: 0,
            hint: r.hint,
            evidence: emptyEvidence(),
        })),
        intensity: 'moderate',
        flavor: 'mixed',
    };
}
